<template>
  <!-- prettier-ignore -->
  <the-section
    :class="classes"
    is-dap
  >
    <template
      v-if="$slots.cardTitle"
      #title>
      <div
        :class="titleClasses"
        @click="toggleContent"
      >
        <slot
          name="cardTitle"
        />
        <div
          v-if="isAccordion"
          :class="iconClasses"
        >
          <the-icon
            name="chevron-down"
            art="light"
          />
        </div>
      </div>
    </template>

    <transition
      name="slide-down"
      mode="out-in"
      @enter="enter"
      @after-leave="afterEnter"
      @leave="leave"
    >
      <div
        v-show="isOpen"
        class="submit-card__content"
      >
        <slot name="content"></slot>
      </div>
    </transition>
  </the-section>
</template>

<script setup>
import { computed, ref, watch } from 'vue'

import useHeightTransition from '@/directives/HeightTransition'

const { enter, afterEnter, leave } = useHeightTransition()

const props = defineProps({
  isAccordion: {
    type: Boolean,
    default() {
      return false
    },
  },

  open: {
    type: Boolean,
    default() {
      return true
    },
  },

  invalid: {
    type: Boolean,
    default: false,
  },
})

// data
const isOpen = ref(props.open)

const $emit = defineEmits(['toggle'])

// watchers
watch(
  () => props.open,
  value => {
    isOpen.value = value
  }
)

// computed
const classes = computed(() => {
  return {
    'submit-card': true,
    'submit-card--invalid': props.invalid,
  }
})

const titleClasses = computed(() => {
  return {
    'submit-card__title': true,
    'submit-card__title--only': props.isAccordion && !isOpen.value,
  }
})

const iconClasses = computed(() => {
  return {
    'submit-card__icon': true,
    'submit-card__icon--rotate': isOpen.value,
  }
})

// methods
function toggleContent() {
  $emit('toggle', !isOpen.value)
}
</script>

<style scoped>
.submit-card {
  background-color: var(--c-primary-neutral-3);
  border: 1px solid transparent;
  transition: border-color 0.3s ease-in-out;
}

.submit-card__title {
  position: relative;
}

.submit-card__title--only {
  margin-bottom: 0;
}

.submit-card__icon {
  position: absolute;
  top: 0;
  right: 0;
  transform: rotate(0deg);
  transition-duration: 0.3s;

  .submit-card__icon--rotate {
    transform: rotate(180deg);
    transition-duration: 0.3s;
  }
}

.submit-card__content {
  font-size: 15px;
  line-height: 20px;
  display: block;
  transition: 0.3s ease-out;
}

.submit-card--invalid {
  border-color: var(--c-secondary-color-1);

  .submit-card__title {
    color: var(--c-secondary-color-1);
  }
}
</style>

<style name="animations" scoped>
.slide-down-enter-active,
.slide-down-leave-active,
.slide-up-enter-active,
.slide-up-leave-active {
  transition-duration: 0.5s;
  transition-property: height, opacity, transform;
  transition-timing-function: cubic-bezier(0.55, 0, 0.1, 1);
  overflow: hidden;
}

.slide-down-enter,
.slide-up-leave-active,
.slide-down-leave-active,
.slide-up-enter {
  opacity: 0;
  transform: translate(0, -2em);
}
</style>
